/**
 * Module: `queries/conversation-content.ts` — client library utilities.
 * @remarks See exports; keep imports from concrete files when possible.
 */

import { DeleteOperationItemNotFoundError } from "@tanstack/query-db-collection";
import type { QueryClient } from "@tanstack/react-query";

import { apiClient, extractMessagesFromDustResponse } from "@/lib/api";
import {
  normalizeGatekeeperTranscript,
  type GatekeeperPrecisionDbItem,
} from "@/lib/qualification-lifecycle/normalize-transcript";
import { mergeInProgressSnapshotIntoMessages } from "@/lib/queries/conversation-in-progress-merge";
import { syncConversationQuestionsFromContent } from "@/lib/queries/conversation-questions";
import { applyGatekeeperPrecisionsFromDatabase } from "@/lib/qualification-lifecycle/persist-precisions";
import {
  conversationQueryOptions,
  isEphemeralChatConversationId,
  isMockDustConversationId,
} from "@/lib/queries/conversation";
import { recordConversationContentNetworkFetch } from "@/lib/queries/conversation-cache-policy";
import {
  conversationContentCollection,
  conversationsCollection,
} from "@/tanstack-db/collections";
import type { ConversationContentRow } from "@/tanstack-db/schemas";
import type {
  ExtractedConversationData,
  InProgressMessage,
  Message,
  MessageChainOfThought,
} from "@/types";

const pendingUpserts = new Map<string, ConversationContentRow>();
const pendingDeletes = new Set<string>();
let flushRetryTimer: ReturnType<typeof setTimeout> | null = null;

function safeWriteDeleteConversationContent(conversationId: string): void {
  try {
    conversationContentCollection.utils.writeDelete(conversationId);
  } catch (error) {
    if (error instanceof DeleteOperationItemNotFoundError) return;
    throw error;
  }
}

function flushPendingWrites(): void {
  if (!conversationContentCollection.isReady()) return;

  if (pendingDeletes.size > 0) {
    for (const conversationId of pendingDeletes) {
      safeWriteDeleteConversationContent(conversationId);
      pendingUpserts.delete(conversationId);
    }
    pendingDeletes.clear();
  }

  if (pendingUpserts.size > 0) {
    conversationContentCollection.utils.writeUpsert([
      ...pendingUpserts.values(),
    ]);
    pendingUpserts.clear();
  }
}

function scheduleFlushPendingWrites(): void {
  if (flushRetryTimer) return;
  flushRetryTimer = setTimeout(() => {
    flushRetryTimer = null;
    if (!conversationContentCollection.isReady()) {
      scheduleFlushPendingWrites();
      return;
    }
    flushPendingWrites();
  }, 50);
}

export function conversationContentRowFromExtracted(
  conversationId: string,
  extracted: ExtractedConversationData,
  options?: { stale?: boolean }
): ConversationContentRow {
  return {
    conversationId,
    ...extracted,
    stale: options?.stale ?? false,
    lastFetchedAt: new Date().toISOString(),
  };
}

export function writeConversationContentRow(row: ConversationContentRow): void {
  if (conversationContentCollection.isReady()) {
    conversationContentCollection.utils.writeUpsert([row]);
    return;
  }
  pendingDeletes.delete(row.conversationId);
  pendingUpserts.set(row.conversationId, row);
  scheduleFlushPendingWrites();
}

/** Next hydration will refetch the Dust snapshot for this conversation. */
export function markConversationContentStale(conversationId: string): void {
  const existing = conversationContentCollection.get(conversationId);
  if (existing) {
    conversationContentCollection.utils.writeUpsert([
      { ...existing, stale: true },
    ]);
  }
}

/** Drop cached transcript when the conversation is deleted locally. */
export function deleteConversationContent(conversationId: string): void {
  pendingUpserts.delete(conversationId);
  pendingDeletes.delete(conversationId);

  if (conversationContentCollection.isReady()) {
    safeWriteDeleteConversationContent(conversationId);
    return;
  }

  pendingDeletes.add(conversationId);
  scheduleFlushPendingWrites();
}

/**
 * Fetch Dust snapshot when missing or stale; writes TanStack DB (source of truth).
 * Uses React Query fetchQuery only as a network dedupe layer.
 */
export type ConversationContentHydrateOptions = {
  force?: boolean;
  /**
   * When the agent stream just finished locally, ignore Dust "created" status so
   * we do not re-arm resume-stream in a loop.
   */
  clearInProgressMessage?: boolean;
};

const postStreamRefreshInFlight = new Set<string>();
const inFlightHydration = new Map<string, Promise<void>>();

/**
 * After a local stream ends, Dust GET may still return succeeded with empty content.
 * Keep the longer local transcript and merge server-only fields (e.g. duration).
 */
export function mergeExtractedWithCachedConversationContent(
  conversationId: string,
  extracted: ExtractedConversationData,
  gatekeeperDbItems: GatekeeperPrecisionDbItem[] = [],
  options?: { preserveInProgress?: boolean }
): ExtractedConversationData {
  const cached = conversationContentCollection.get(conversationId);
  if (!cached?.messages.length) {
    const inProgress = extracted.inProgressMessage ?? null;
    const messages = mergeInProgressSnapshotIntoMessages(
      normalizeGatekeeperTranscript(extracted.messages, gatekeeperDbItems),
      inProgress
    );
    return {
      ...extracted,
      messages,
      inProgressMessage: options?.preserveInProgress ? inProgress : null,
    };
  }

  const cachedById = new Map(
    cached.messages.map((m) => [String(m.id), m] as const)
  );
  const gatekeeperPrecisionsByContent = new Map(
    cached.messages
      .filter((m) => m.gatekeeperPrecisions?.length)
      .map((m) => [m.content, m.gatekeeperPrecisions!] as const)
  );
  const mergedIds = new Set<string>();

  const messages = extracted.messages.map((serverMsg) => {
    const id = String(serverMsg.id);
    mergedIds.add(id);
    const local = cachedById.get(id);
    const precisionsFromContent =
      serverMsg.role === "user"
        ? gatekeeperPrecisionsByContent.get(serverMsg.content)
        : undefined;

    if (!local) {
      if (!precisionsFromContent?.length) return serverMsg;
      return {
        ...serverMsg,
        gatekeeperPrecisions: precisionsFromContent,
      };
    }

    const serverLen = serverMsg.content?.length ?? 0;
    const localLen = local.content?.length ?? 0;

    return {
      ...serverMsg,
      content: localLen > serverLen ? local.content : serverMsg.content,
      completionDurationMs:
        serverMsg.completionDurationMs ?? local.completionDurationMs,
      status:
        local.status === "cancelled" || local.status === "failed"
          ? local.status
          : (serverMsg.status ?? local.status),
      error: serverMsg.error ?? local.error,
      userIntent: local.userIntent ?? serverMsg.userIntent,
      assistantOrigin: local.assistantOrigin ?? serverMsg.assistantOrigin,
      gatekeeperQualificationKind:
        local.gatekeeperQualificationKind ??
        serverMsg.gatekeeperQualificationKind,
      gatekeeperPrecisions:
        local.gatekeeperPrecisions ??
        serverMsg.gatekeeperPrecisions ??
        precisionsFromContent,
      gatekeeperPhase: local.gatekeeperPhase ?? serverMsg.gatekeeperPhase,
    };
  });

  for (const local of cached.messages) {
    const id = String(local.id);
    if (!mergedIds.has(id)) {
      const contentAlreadyMerged = messages.some(
        (m) => m.role === "user" && m.content === local.content
      );
      if (contentAlreadyMerged && local.gatekeeperPrecisions?.length) {
        const matchIndex = messages.findIndex(
          (m) => m.role === "user" && m.content === local.content
        );
        if (matchIndex >= 0) {
          messages[matchIndex] = {
            ...messages[matchIndex],
            gatekeeperPrecisions:
              local.gatekeeperPrecisions ??
              messages[matchIndex].gatekeeperPrecisions,
            gatekeeperPhase:
              local.gatekeeperPhase ?? messages[matchIndex].gatekeeperPhase,
            userIntent: local.userIntent ?? messages[matchIndex].userIntent,
          };
        }
        continue;
      }
      messages.push(local);
    }
  }

  messages.sort((a, b) => a.timestamp.getTime() - b.timestamp.getTime());

  const dbItems =
    gatekeeperDbItems.length > 0
      ? gatekeeperDbItems
      : cached.messages
          .filter((m) => m.gatekeeperPrecisions?.length)
          .map((m) => ({
            dustMessageId: String(m.id),
            gatekeeperPrecisions: m.gatekeeperPrecisions!,
          }));

  const inProgress = options?.preserveInProgress
    ? (extracted.inProgressMessage ?? cached.inProgressMessage ?? null)
    : null;
  const normalized = normalizeGatekeeperTranscript(messages, dbItems);
  return {
    ...extracted,
    messages: mergeInProgressSnapshotIntoMessages(normalized, inProgress),
    inProgressMessage: inProgress,
    allChainOfThoughts:
      extracted.allChainOfThoughts.length >= cached.allChainOfThoughts.length
        ? extracted.allChainOfThoughts
        : cached.allChainOfThoughts,
  };
}

/**
 * Absolute delays from schedule time. Dust often keeps the provisional
 * user-question title for a few seconds before the synthetic title lands.
 */
const POST_STREAM_REFRESH_AT_MS = [800, 2500, 6000] as const;

function delay(ms: number): Promise<void> {
  return new Promise((resolve) => {
    window.setTimeout(resolve, ms);
  });
}

/**
 * Delayed force GET(s) after stream end. Syncs Dust synthetic title into
 * conversationsCollection (and Sidoni DB via PATCH / ensureConversationFromDust).
 * Retries while the title is still the pre-stream value. Does not mark stale
 * (avoids hydrate loops). Clears in-progress on write so resume-stream is
 * not re-triggered.
 */
export function schedulePostStreamConversationRefresh(
  client: QueryClient,
  conversationId: string,
  onSettled?: () => void
): void {
  if (!conversationId || postStreamRefreshInFlight.has(conversationId)) return;
  postStreamRefreshInFlight.add(conversationId);

  const titleAtSchedule =
    conversationsCollection.get(conversationId)?.title?.trim() ?? "";

  void (async () => {
    try {
      const runAttempt = async (
        index: number,
        elapsed: number
      ): Promise<void> => {
        if (index >= POST_STREAM_REFRESH_AT_MS.length) return;
        const target = POST_STREAM_REFRESH_AT_MS[index];
        await delay(target - elapsed);

        await ensureConversationContentHydrated(client, conversationId, {
          force: true,
          clearInProgressMessage: true,
        });

        const titleNow =
          conversationsCollection.get(conversationId)?.title?.trim() ?? "";
        if (titleNow && titleNow !== titleAtSchedule) return;
        await runAttempt(index + 1, target);
      };
      await runAttempt(0, 0);
    } finally {
      postStreamRefreshInFlight.delete(conversationId);
      onSettled?.();
    }
  })();
}

export async function ensureConversationContentHydrated(
  client: QueryClient,
  conversationId: string,
  options?: ConversationContentHydrateOptions
): Promise<void> {
  if (!conversationId) return;
  if (isEphemeralChatConversationId(conversationId)) return;

  const previous = inFlightHydration.get(conversationId);
  const run = (async () => {
    if (previous) {
      await previous.catch(() => {});
      if (!options?.force) return;
    }
    await hydrateConversationContent(client, conversationId, options);
  })();
  inFlightHydration.set(conversationId, run);
  try {
    await run;
  } finally {
    if (inFlightHydration.get(conversationId) === run) {
      inFlightHydration.delete(conversationId);
    }
  }
}

async function hydrateConversationContent(
  client: QueryClient,
  conversationId: string,
  options?: ConversationContentHydrateOptions
): Promise<void> {
  if (!options?.force) {
    const existing = conversationContentCollection.get(conversationId);
    if (existing && !existing.stale) return;
  }

  if (isMockDustConversationId(conversationId)) {
    const existing = conversationContentCollection.get(conversationId);
    if (existing) {
      writeConversationContentRow({ ...existing, stale: false });
      return;
    }
    writeConversationContentRow(
      conversationContentRowFromExtracted(
        conversationId,
        {
          messages: [],
          allChainOfThoughts: [],
          lastAgentConfigurationId: null,
          inProgressMessage: null,
        },
        { stale: false }
      )
    );
    return;
  }

  recordConversationContentNetworkFetch(conversationId);
  // force: bypass React Query staleTime so post-stream retries hit Dust again
  // (synthetic title + completed content often arrive after the first GET).
  const dust = await client.fetchQuery({
    ...conversationQueryOptions(conversationId),
    ...(options?.force ? { staleTime: 0 } : {}),
  });

  // Dust generates a synthetic conversation title asynchronously. Whenever we
  // fetch the conversation snapshot, propagate that title to conversationsCollection
  // so the sidebar and header reflect it without a separate API call.
  const dustTitle = dust?.title?.trim();
  const existingConversation = conversationsCollection.get(conversationId);
  if (
    dustTitle &&
    existingConversation &&
    existingConversation.title?.trim() !== dustTitle
  ) {
    conversationsCollection.update(conversationId, (draft) => {
      draft.title = dustTitle;
    });
  }

  let extracted = extractMessagesFromDustResponse(dust);
  let gatekeeperDbItems: {
    dustMessageId: string;
    gatekeeperPrecisions: import("@/types").GatekeeperPrecisionPair[];
  }[] = [];
  try {
    const gatekeeperMetadata =
      await apiClient.getGatekeeperPrecisions(conversationId);
    gatekeeperDbItems = gatekeeperMetadata.items;
    extracted = {
      ...extracted,
      messages: applyGatekeeperPrecisionsFromDatabase(
        extracted.messages,
        gatekeeperDbItems
      ),
    };
  } catch {
    // Best-effort: Dust transcript still loads without gatekeeper metadata.
  }

  if (options?.clearInProgressMessage) {
    extracted = mergeExtractedWithCachedConversationContent(
      conversationId,
      extracted,
      gatekeeperDbItems
    );
  } else if (extracted.inProgressMessage) {
    extracted = mergeExtractedWithCachedConversationContent(
      conversationId,
      extracted,
      gatekeeperDbItems,
      { preserveInProgress: true }
    );
  } else {
    extracted = {
      ...extracted,
      messages: normalizeGatekeeperTranscript(
        extracted.messages,
        gatekeeperDbItems
      ),
    };
  }
  writeConversationContentRow(
    conversationContentRowFromExtracted(conversationId, extracted, {
      stale: false,
    })
  );
  syncConversationQuestionsFromContent(conversationId);
}

export function persistConversationContentFromExtracted(
  conversationId: string,
  extracted: ExtractedConversationData
): void {
  writeConversationContentRow(
    conversationContentRowFromExtracted(conversationId, extracted, {
      stale: false,
    })
  );
}

export function persistConversationContentFromUiState(params: {
  conversationId: string;
  messages: Message[];
  allChainOfThoughts: MessageChainOfThought[];
  inProgressMessage: InProgressMessage | null;
  lastAgentConfigurationId?: string | null;
}): void {
  const extracted: ExtractedConversationData = {
    messages: params.messages,
    allChainOfThoughts: params.allChainOfThoughts,
    lastAgentConfigurationId: params.lastAgentConfigurationId ?? null,
    inProgressMessage: params.inProgressMessage,
  };
  persistConversationContentFromExtracted(params.conversationId, extracted);
}
